/**
 * React Query hooks for backtest data fetching.
 * Provides declarative data fetching with caching and automatic refetching.
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getStrategies,
  getStrategy,
  getHistory,
  getBacktest,
  deleteBacktest,
  deleteAllHistory,
  getCandles,
  getExchanges,
  getSymbols,
  getAggregations,
  createAggregation,
  updateAggregation,
  deleteAggregation,
  ApiClientError,
} from '../api/client';
import { useBacktestStore } from '../stores/backtestStore';
import { useOpenedRunsStore } from '../stores/openedRunsStore';
import { usePortfoliosStore } from '../stores/portfoliosStore';
import type {
  CandleRequest,
  StrategyInfo,
  StrategyDetails,
  ApiBacktestResult,
  Candle,
  PaginatedHistory,
  AggregationConfig,
  CreateAggregationRequest,
  UpdateAggregationRequest,
} from '../types';

// Query keys for cache management
export const queryKeys = {
  strategies: ['strategies'] as const,
  strategy: (name: string) => ['strategy', name] as const,
  history: ['history'] as const,
  backtest: (id: string) => ['backtest', id] as const,
  candles: (params: CandleRequest) => ['candles', params] as const,
  exchanges: ['exchanges'] as const,
  symbols: (exchange: string) => ['symbols', exchange] as const,
  aggregations: ['aggregations'] as const,
  aggregation: (id: string) => ['aggregation', id] as const,
};

// ============================================================================
// Strategy Hooks
// ============================================================================

/**
 * Fetch list of all available strategies
 */
export function useStrategies() {
  return useQuery<StrategyInfo[], Error>({
    queryKey: queryKeys.strategies,
    queryFn: getStrategies,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch details for a specific strategy
 */
export function useStrategy(name: string) {
  return useQuery<StrategyDetails, Error>({
    queryKey: queryKeys.strategy(name),
    queryFn: () => getStrategy(name),
    enabled: !!name, // Only fetch when name is provided
    staleTime: 1000 * 60 * 5,
  });
}

// ============================================================================
// History Hooks
// ============================================================================

const PAGE_SIZE = 10;

/**
 * Fetch backtest history with infinite loading
 */
export function useHistory() {
  return useInfiniteQuery<PaginatedHistory, Error>({
    queryKey: queryKeys.history,
    queryFn: ({ pageParam = 0 }) => getHistory({ limit: PAGE_SIZE, offset: pageParam as number, runType: 'strategies' }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined;
      const totalLoaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return totalLoaded;
    },
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Fetch a specific backtest result
 */
export function useBacktest(id: string | null) {
  return useQuery<ApiBacktestResult, Error>({
    queryKey: queryKeys.backtest(id || ''),
    queryFn: () => getBacktest(id!),
    enabled: !!id,
    staleTime: Infinity, // Backtest results don't change
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Delete a backtest mutation
 */
export function useDeleteBacktest() {
  const queryClient = useQueryClient();
  const { currentResult, clear } = useBacktestStore();
  const unpin = useOpenedRunsStore((s) => s.unpin);

  return useMutation<void, Error, string>({
    mutationFn: deleteBacktest,
    onSuccess: (_, deletedId) => {
      // Invalidate every view that can render a list of backtest runs — a run
      // can appear in the flat explorer list, the "group by asset" expanded
      // groups, a strategy config's runs tab, or a portfolio's recent-runs
      // section — all must reflect the deletion immediately (SHA-43: deleting a
      // run was leaving it visible in the UI because these lists weren't all
      // being invalidated on the same delete). Query keys are invalidated by
      // prefix (React Query does partial matching), so this covers every
      // parameterization (any configId / aggregationId / filter combination).
      queryClient.invalidateQueries({ queryKey: queryKeys.history });
      queryClient.invalidateQueries({ queryKey: ['explorer-history'] });
      queryClient.invalidateQueries({ queryKey: ['explorer-groups'] });
      queryClient.invalidateQueries({ queryKey: ['group-runs'] });
      queryClient.invalidateQueries({ queryKey: ['aggregation-runs'] });
      queryClient.invalidateQueries({ queryKey: ['strategy-config-runs'] });
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.backtest(deletedId) });
      // Remove from the sidebar's "opened runs" pin list — otherwise a deleted
      // run lingers there forever (it's persisted to localStorage and never
      // gets invalidated by the history query cache).
      unpin(deletedId);
      // Clear current result if it was deleted
      if (currentResult?.id === deletedId) {
        clear();
      }
    },
  });
}

/**
 * Delete all backtest history mutation
 */
export function useDeleteAllHistory() {
  const queryClient = useQueryClient();
  const { clear } = useBacktestStore();

  return useMutation<{ message: string; count: number }, Error, void>({
    mutationFn: deleteAllHistory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.history });
      queryClient.invalidateQueries({ queryKey: ['explorer-history'] });
      clear();
    },
  });
}

// ============================================================================
// Data Hooks
// ============================================================================

/**
 * Fetch candle data
 */
export function useCandles(params: CandleRequest | null) {
  return useQuery<Candle[], Error>({
    queryKey: queryKeys.candles(params!),
    queryFn: () => getCandles(params!),
    enabled: !!params,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Fetch available exchanges
 */
export function useExchanges() {
  return useQuery<string[], Error>({
    queryKey: queryKeys.exchanges,
    queryFn: getExchanges,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Fetch symbols for an exchange
 */
export function useSymbols(exchange: string) {
  return useQuery<string[], Error>({
    queryKey: queryKeys.symbols(exchange),
    queryFn: () => getSymbols(exchange),
    enabled: !!exchange,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Fetch candles at a specific resolution for multi-resolution chart
 */
export function useResolutionCandles(params: CandleRequest | null) {
  return useQuery<Candle[], Error>({
    queryKey: ['resolution-candles', params],
    queryFn: () => getCandles(params!),
    enabled: !!params,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Fetch funding rates for a given exchange/symbol/date range
 */
export function useFundingRates(params: {
  exchange: string;
  symbol: string;
  start: number;
  end: number;
} | null) {
  return useQuery({
    queryKey: ['funding-rates', params],
    queryFn: async () => {
      if (!params) return [];
      const { getFundingRates } = await import('../api/client');
      const data = await getFundingRates(params);
      return data.rates;
    },
    enabled: !!params,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

// ============================================================================
// Aggregation Hooks
// ============================================================================

/**
 * Fetch all aggregation configs
 */
export function useAggregations() {
  return useQuery<AggregationConfig[], Error>({
    queryKey: queryKeys.aggregations,
    queryFn: getAggregations,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Create a new aggregation
 */
export function useCreateAggregation() {
  const queryClient = useQueryClient();
  return useMutation<AggregationConfig, Error, CreateAggregationRequest>({
    mutationFn: (config) => createAggregation(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.aggregations });
    },
  });
}

/**
 * Update an aggregation
 */
export function useUpdateAggregation() {
  const queryClient = useQueryClient();
  return useMutation<AggregationConfig, Error, { id: string; updates: UpdateAggregationRequest }>({
    mutationFn: ({ id, updates }) => updateAggregation(id, updates),
    onSuccess: (_result, { id }) => {
      // Invalidate the aggregations list, the portfolios filtered view, and the
      // individual aggregation detail — all three can display the name.
      queryClient.invalidateQueries({ queryKey: queryKeys.aggregations });
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      queryClient.invalidateQueries({ queryKey: ['aggregation', id] });
    },
  });
}

/**
 * Delete an aggregation (SHA-43 portfolio CRUD).
 *
 * Invalidates every cache that can render an aggregation — the flat aggregations
 * list, the portfolios sidebar, and the individual detail view. Also clears the
 * selectedPortfolioId in portfoliosStore when the deleted portfolio was selected,
 * so the sidebar doesn't keep highlighting a deleted entry.
 */
export function useDeleteAggregation() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteAggregation(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.aggregations });
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      queryClient.removeQueries({ queryKey: ['aggregation', deletedId] });
      queryClient.invalidateQueries({ queryKey: ['aggregation-runs', deletedId] });
      queryClient.invalidateQueries({ queryKey: ['aggregation-optimizations', deletedId] });
      queryClient.invalidateQueries({ queryKey: ['aggregation-paper-sessions', deletedId] });

      // Clear the selected portfolio in the sidebar store if it was the deleted one
      // so the detail panel doesn't stay on a deleted portfolio.
      const { selectedPortfolioId, setSelectedPortfolioId } = usePortfoliosStore.getState();
      if (selectedPortfolioId === deletedId) {
        setSelectedPortfolioId(null);
      }
    },
  });
}

// ============================================================================
// Combined Hook - Load backtest from history
// ============================================================================

/**
 * Load a backtest from history and set it as current
 */
export function useLoadBacktest() {
  const queryClient = useQueryClient();
  const { setResult, setError, setSelectedBacktestId, setRunNotFound } = useBacktestStore();
  const unpin = useOpenedRunsStore((s) => s.unpin);

  const loadBacktest = async (id: string) => {
    try {
      setSelectedBacktestId(id);

      // Check if already cached
      const cached = queryClient.getQueryData<ApiBacktestResult>(
        queryKeys.backtest(id)
      );

      if (cached) {
        setResult(cached);
        return cached;
      }

      // Fetch from API
      const result = await queryClient.fetchQuery({
        queryKey: queryKeys.backtest(id),
        queryFn: () => getBacktest(id),
      });

      setResult(result);
      return result;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to load backtest';
      setError(message);

      // 404 (run deleted / stale deep-link): mark it as "not found" so the UI
      // can render a graceful state instead of a blank panel, clear the stale
      // result, and clean up any lingering sidebar pin for this id.
      if (error instanceof ApiClientError && error.statusCode === 404) {
        setRunNotFound(id);
        useBacktestStore.setState({ currentResult: null });
        unpin(id);
        queryClient.removeQueries({ queryKey: queryKeys.backtest(id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.history });
        queryClient.invalidateQueries({ queryKey: ['explorer-history'] });
      }

      throw error;
    }
  };

  return { loadBacktest };
}
